/**
 * Dark-launch gate for the Recruitment / Onboarding / Vendor stack.
 *
 * Asks the backend whether the tenant's recruitment feature flag is on.
 * FAILS CLOSED: while loading, on any error, or on a malformed response the
 * feature is reported as disabled, so nothing from the stack is ever shown
 * unless the backend explicitly answers `{"enabled": true}`.
 */
import useSWR from 'swr';
import { BaseAPI } from 'src/providers/base-api';

interface RecruitmentFeatureResponse {
  enabled?: boolean;
}

const fetchRecruitmentFeature = async (): Promise<boolean> => {
  const response = await BaseAPI.request('GET', '/v1/recruitment/feature', null, undefined);
  if (response?.status === 200 && response?.data) {
    // Go responses may or may not be wrapped in { data: ... }
    const body: RecruitmentFeatureResponse = response.data.data ?? response.data;
    return body?.enabled === true;
  }
  throw new Error(response?.message || 'Failed to load recruitment feature flag');
};

/**
 * `skip` (opt-in, default false) makes the hook inert: no request, `enabled: false`,
 * `isLoading: false`. Used by callers that only sometimes need the flag.
 */
export function useRecruitmentFeature(options?: { skip?: boolean }): { enabled: boolean; isLoading: boolean } {
  const skip = options?.skip === true;
  const { data, error, isLoading } = useSWR<boolean>(skip ? null : '/v1/recruitment/feature', fetchRecruitmentFeature, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
    shouldRetryOnError: true,
    errorRetryCount: 2,
  });
  return {
    enabled: !skip && !error && data === true,
    isLoading: isLoading && data === undefined && !error,
  };
}

export default useRecruitmentFeature;
